/**
 * features/attendance/AttendanceModule.tsx
 * ------------------------------------------------------------------
 * Round 13 — Attendance hub with role-aware tabs.
 *
 *   Entry            — daily attendance entry (existing DailyAttendance)
 *                      Visible to: class teachers + admin + headteacher
 *
 *   Per-class        — per-class attendance summary with drill-down
 *                      Visible to: every staff role (not parents)
 *
 *   School-wide      — overall attendance % across the school
 *                      Visible to: admin + headteacher only
 *
 *   Absent today     — live list of absentees with parent phone numbers
 *                      Visible to: admin + headteacher + HOY
 *                      (Phone numbers shown to admin + HOY)
 *
 * Tabs are computed from the signed-in user's role; only allowed tabs
 * appear. The initial tab depends on role too — teachers default to
 * Entry, leadership defaults to School-wide.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { ClipboardList, BarChart3, Users, AlertCircle, FileText, type LucideIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { canEnterClassCommentFor } from '@/lib/permissions';
import AttendanceEntryTabs from './AttendanceEntryTabs';
import PerClassOverview from './PerClassOverview';
import SchoolWideOverview from './SchoolWideOverview';
import AbsentToday from './AbsentToday';
import AttendanceRegisters from './AttendanceRegisters';

type TabKey = 'entry' | 'per-class' | 'registers' | 'school' | 'absent-today';

interface TabDef {
  key: TabKey;
  label: string;
  icon: LucideIcon;
}

const ALL_TABS: TabDef[] = [
  { key: 'entry', label: 'Entry', icon: ClipboardList },
  { key: 'per-class', label: 'Per-class', icon: Users },
  { key: 'registers', label: 'Registers', icon: FileText },
  { key: 'school', label: 'School-wide', icon: BarChart3 },
  { key: 'absent-today', label: 'Absent today', icon: AlertCircle },
];

export default function AttendanceModule() {
  const { userData } = useAuth();
  // Gate on the user's FULL role set, not just the primary role: class
  // teachers / HODs / HOYs keep 'subjectTeacher' as their primary role and
  // carry the extra role in roles[]. Checking only userData.role hid the
  // Entry tab from class teachers.
  const { classes, teachers } = useData();
  const roles = useMemo(
    () => (userData?.roles?.length ? userData.roles : userData?.role ? [userData.role] : []),
    [userData],
  );
  const has = (r: string) => (roles as string[]).includes(r);

  // Can this user enter attendance for ANY class? True for admin/headteacher,
  // academic/system admins, and anyone ASSIGNED as a class's (assistant) class
  // teacher — even if their account carries only subjectTeacher/hod. Drives
  // whether the Entry tab appears at all.
  const canEnterAny = useMemo(
    () => classes.some((c) => canEnterClassCommentFor(userData, c.id, classes, teachers)),
    [classes, teachers, userData],
  );

  const visibleTabs = useMemo<TabDef[]>(() => {
    const allowed = new Set<TabKey>();
    // Entry (mark a class's daily register): anyone who can enter attendance
    // for at least one class — assigned class teachers (incl. those stored as
    // subjectTeacher/hod), admin/headteacher, and academic/system admins.
    if (canEnterAny) {
      allowed.add('entry');
    }
    // Per-class (summary across ALL classes) is a supervisor/manager overview.
    if (has('admin') || has('headteacher') || has('academicAdmin') || has('hod') || has('hoy') || has('editor')) {
      allowed.add('per-class');
    }
    // Registers (printable PDF from existing data): anyone who can enter a
    // class's register OR see the per-class overview.
    if (canEnterAny || has('admin') || has('headteacher') || has('academicAdmin') || has('hod') || has('hoy') || has('editor')) {
      allowed.add('registers');
    }
    // School-wide: admin + headteacher + academic admin
    if (has('admin') || has('headteacher') || has('academicAdmin')) {
      allowed.add('school');
    }
    // Absent today: admin + headteacher + academic admin + HOY
    if (has('admin') || has('headteacher') || has('academicAdmin') || has('hoy')) {
      allowed.add('absent-today');
    }
    return ALL_TABS.filter((t) => allowed.has(t.key));
  }, [roles, canEnterAny]);

  // Default tab: leadership starts on School-wide if available, else Per-class.
  // Everyone else starts on Entry if available, else Per-class.
  const defaultTab = useMemo<TabKey>(() => {
    if (has('admin') || has('headteacher') || has('academicAdmin')) {
      if (visibleTabs.some((t) => t.key === 'school')) return 'school';
    }
    if (visibleTabs.some((t) => t.key === 'entry')) return 'entry';
    if (visibleTabs.some((t) => t.key === 'per-class')) return 'per-class';
    return visibleTabs[0]?.key ?? 'entry';
  }, [roles, visibleTabs]);

  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab);

  if (visibleTabs.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        You don't have permission to view attendance.
      </div>
    );
  }

  return (
    <div>
      {/* Tab strip */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4 sticky top-0 bg-white dark:bg-gray-900 z-10">
        <div className="flex gap-0 overflow-x-auto px-1 -mx-1">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const active = tab.key === activeTab;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={[
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap',
                  'border-b-2 transition-colors',
                  active
                    ? 'border-blue-600 text-blue-700 dark:text-blue-300 dark:border-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-300',
                ].join(' ')}
                aria-current={active ? 'page' : undefined}
              >
                <Icon size={16} className={active ? '' : 'opacity-70'} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active tab content */}
      {activeTab === 'entry' && <AttendanceEntryTabs />}
      {activeTab === 'per-class' && <PerClassOverview />}
      {activeTab === 'registers' && <AttendanceRegisters />}
      {activeTab === 'school' && <SchoolWideOverview />}
      {activeTab === 'absent-today' && <AbsentToday />}
    </div>
  );
}
